/**
 * NativePurchaseGuard — route-level gate for pages whose ENTIRE purpose is
 * purchase (pricing pages, checkout). In the native Capacitor build these pages
 * must not render at all (Pure Client v1 — no price, no buy button anywhere).
 *
 * On web it is a pure pass-through (renders `children` unchanged). In native it
 * either:
 *   - redirects to a safe route (default `/` — recommended for pricing pages a
 *     free user could deep-link into), or
 *   - renders the neutral {@link WebManagedPlanNotice} panel in place of the
 *     page (use `mode="replace"` when a redirect would be jarring, e.g. a page
 *     the user was intentionally sent to).
 *
 * See `docs/mobile-app/STORE-PURCHASE-STRATEGY.md` §(b)/(c).
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useIsNativeApp } from './useIsNativeApp';
import { WebManagedPlanNotice } from './WebManagedPlanNotice';

interface NativePurchaseGuardProps {
  children: React.ReactNode;
  /**
   * `redirect` (default) — native users are sent to `redirectTo`.
   * `replace` — native users see the neutral "managed on the web" panel in
   * place of the page content (kept inside whatever layout wraps this route).
   */
  mode?: 'redirect' | 'replace';
  /** Redirect target for `mode="redirect"`. Defaults to home. */
  redirectTo?: string;
  /** Optional title override for the `replace` panel. */
  title?: string;
}

export function NativePurchaseGuard({
  children,
  mode = 'redirect',
  redirectTo = '/',
  title,
}: NativePurchaseGuardProps) {
  const isNative = useIsNativeApp();

  if (!isNative) {
    return <>{children}</>;
  }

  if (mode === 'replace') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4 py-12">
        <WebManagedPlanNotice variant="panel" title={title} />
      </div>
    );
  }

  return <Navigate to={redirectTo} replace />;
}
